import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'

// ==================== Types ====================

export interface AdminStats {
  templates: { total: number; enabled: number; disabled: number }
  categories: { total: number; enabled: number; disabled: number }
  generations: { today: number; thisWeek: number; thisMonth: number }
  users: { total: number; admins: number }
}

export interface DailyStatItem {
  date: string
  videoCount: number
  generationCount: number
  sharedCount: number
}

export interface DailyGenerationStats {
  dailyStats: DailyStatItem[]
  summary: {
    totalVideos: number
    totalGenerations: number
    totalShared: number
    averagePerDay: number
    peakDay: {
      date: string
      count: number
    }
  }
}

export interface DailyUserStatItem {
  date: string
  newUsers: number
}

export interface DailyUserStats {
  dailyStats: DailyUserStatItem[]
  summary: {
    totalNewUsers: number
    averagePerDay: number
    peakDay: {
      date: string
      count: number
    }
  }
}

export interface DailyRevenueItem {
  date: string
  costUsdCents: number
  costKrw: number
  revenueKrw: number
  profitKrw: number
  generationCount: number
}

export interface RevenueStats {
  dailyStats: DailyRevenueItem[]
  summary: {
    totalCostKrw: number
    totalRevenueKrw: number
    totalProfitKrw: number
    averageRevenuePerDay: number
    peakRevenueDay: {
      date: string
      revenueKrw: number
    }
    marginPercent: number
  }
}

export interface HourlyStatItem {
  hour: number
  videoCount: number
  generationCount: number
}

export interface HourlyStats {
  date: string
  timezone?: string
  hourlyStats: HourlyStatItem[]
  summary: {
    totalVideos: number
    totalGenerations: number
    peakHour: {
      hour: number
      count: number
    }
  }
}

export interface Template {
  id: string
  categoryId?: string
  name: string
  description?: string
  nameI18n?: Record<string, string>
  descriptionI18n?: Record<string, string>
  textPrompt: string
  textPromptI18n?: Record<string, string>
  promptSuffix?: string
  ratio: string
  resolution?: string
  duration?: number
  thumbnailS3Key?: string
  thumbnailUrl?: string | null
  videoS3Key?: string | null
  videoUrl?: string | null
  listOrder: number
  enabled: boolean
  createdAt: string
  updatedAt: string
  frameImageCount?: number
  usageCount?: number
  category?: { id: string; name: string }
}

export interface TemplateDetail extends Template {
  frameImages: Array<{
    id: string
    templateId: string
    s3Key: string
    thumbnailS3Key: string
    thumbnailUrl: string
    role: 'first_frame' | 'last_frame'
    listOrder: number
    createdAt: string
  }>
}

export interface Pagination {
  total: number
  page: number
  pageSize: number
  totalPages: number
}

export interface PaginatedTemplates {
  items: Template[]
  pagination: Pagination
}

export interface Category {
  id: string
  name: string
  description?: string
  nameI18n?: Record<string, string>
  descriptionI18n?: Record<string, string>
  listOrder: number
  enabled: boolean
  createdAt: string
  updatedAt: string
}

export interface Generation {
  id: string // generationId
  userId: string
  prompt: string
  ratio: string
  resolution?: string
  duration?: number
  cameraFixed?: boolean
  motionStyle?: string
  noAudio?: boolean
  taskId?: string
  frameImages?: Array<{
    s3Key: string
    thumbnailS3Key: string
    role: 'first_frame' | 'last_frame'
    imageUrl: string
    thumbnailUrl: string
  }>
  generatedVideo: {
    s3Key: string
    thumbnailS3Keys: string[]
    durationSeconds: number
    videoUrl: string
    thumbnailUrls?: string[]
    thumbnailUrl?: string | null
  } | null
  status?: string
  costUsdCents: number
  createdAt: string
  deletedAt?: string | null
  user?: {
    id: string
    email: string
  }
  template?: {
    id: string
    name: string
    description: string | null
    thumbnailUrl: string | null
  } | null
}

export interface PaginatedGenerations {
  items: Generation[]
  pagination: Pagination
}

export interface GenerationAttemptErrorLog {
  stage: 'frame-download' | 'video-generation' | 's3-upload' | 'reference-copy' | 'db-insert' | 'unexpected'
  message: string
  imageIndex?: number
  attempt?: number
  maxRetries?: number
  loggedAt: string
}

export interface GenerationAttempt {
  id: string
  generationId: string
  userId: string
  prompt: string
  ratio: string
  resolution?: string
  duration?: number
  cameraFixed?: boolean
  motionStyle?: string
  noAudio?: boolean
  taskId?: string
  frameImages?: Array<{
    s3Key: string
    thumbnailS3Key: string
    role: 'first_frame' | 'last_frame'
  }>
  generatedVideo: {
    s3Key: string
    thumbnailS3Keys: string[]
    durationSeconds: number
  } | null
  templateId?: string | null
  success: boolean
  errorSummary: string
  errorLogs: GenerationAttemptErrorLog[]
  createdAt: string
  user?: {
    id: string
    email: string
  } | null
  template?: {
    id: string
    name: string
  } | null
}

export interface GenerationAttemptDetail {
  id: string
  generationId: string
  userId: string
  prompt: string
  ratio: string
  resolution?: string
  duration?: number
  cameraFixed?: boolean
  motionStyle?: string
  noAudio?: boolean
  taskId?: string
  frameImages?: Array<{
    s3Key: string
    thumbnailS3Key: string
    role: 'first_frame' | 'last_frame'
    imageUrl: string
    thumbnailUrl: string
  }>
  generatedVideo: {
    s3Key: string
    thumbnailS3Keys: string[]
    durationSeconds: number
    videoUrl: string
    thumbnailUrls?: string[]
    thumbnailUrl?: string | null
  } | null
  templateId?: string | null
  success: boolean
  errorSummary: string
  errorLogs: GenerationAttemptErrorLog[]
  createdAt: string
  user?: {
    id: string
    email: string
  } | null
  template?: {
    id: string
    name: string
    description: string | null
    thumbnailUrl: string | null
  } | null
}

export interface PaginatedGenerationAttempts {
  items: GenerationAttempt[]
  pagination: Pagination
}

// ==================== API Client ====================

export const apiClient = api

// ==================== Hooks: Stats ====================

function normalizeDateParam(date: string | null): string | null {
  if (!date) return null
  const trimmed = date.trim()
  if (!trimmed) return null

  const isoDateMatch = trimmed.match(/^(\d{4}-\d{2}-\d{2})/)
  if (isoDateMatch?.[1]) {
    return isoDateMatch[1]
  }

  const parsed = new Date(trimmed)
  if (!Number.isFinite(parsed.getTime())) return null
  return parsed.toISOString().slice(0, 10)
}

export function useAdminStats() {
  return useQuery({
    queryKey: ['admin', 'stats'],
    queryFn: () => apiClient.get<AdminStats>('/admin/stats'),
  })
}

export function useDailyStats(days: number = 14) {
  return useQuery({
    queryKey: ['admin', 'stats', 'daily', days],
    queryFn: () => apiClient.get<DailyGenerationStats>(`/admin/stats/daily?days=${days}`),
  })
}

export function useDailyUserStats(days: number = 14) {
  return useQuery({
    queryKey: ['admin', 'stats', 'daily-users', days],
    queryFn: () => apiClient.get<DailyUserStats>(`/admin/stats/daily-users?days=${days}`),
  })
}

export function useHourlyStats(date: string | null) {
  const normalizedDate = normalizeDateParam(date)

  return useQuery({
    queryKey: ['admin', 'stats', 'hourly', normalizedDate],
    queryFn: () => {
      if (!normalizedDate) {
        throw new Error('Hourly date is required')
      }
      const query = new URLSearchParams({ date: normalizedDate })
      return apiClient.get<HourlyStats>(`/admin/stats/hourly?${query.toString()}`)
    },
    enabled: !!normalizedDate,
  })
}

export function useRevenueStats(days: number = 14) {
  return useQuery({
    queryKey: ['admin', 'stats', 'revenue', days],
    queryFn: () => apiClient.get<RevenueStats>(`/admin/stats/revenue?days=${days}`),
  })
}

// ==================== Hooks: Templates ====================

export function useTemplates(params: {
  page?: number
  pageSize?: number
  search?: string
  enabled?: 'true' | 'false' | 'all'
  categoryId?: string
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
}) {
  const query = new URLSearchParams()
  if (params.page) query.set('page', String(params.page))
  if (params.pageSize) query.set('pageSize', String(params.pageSize))
  if (params.search) query.set('search', params.search)
  if (params.enabled) query.set('enabled', params.enabled)
  if (params.categoryId) query.set('categoryId', params.categoryId)
  if (params.sortBy) query.set('sortBy', params.sortBy)
  if (params.sortOrder) query.set('sortOrder', params.sortOrder)

  return useQuery({
    queryKey: ['admin', 'templates', params],
    queryFn: () => apiClient.get<PaginatedTemplates>(`/admin/templates?${query}`),
  })
}

export function useTemplate(id: string) {
  return useQuery({
    queryKey: ['admin', 'templates', id],
    queryFn: () => apiClient.get<TemplateDetail>(`/admin/templates/${id}`),
    enabled: !!id,
  })
}

export function useCreateTemplate() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: Partial<Template>) => apiClient.post<Template>('/admin/templates', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'templates'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'stats'] })
    },
  })
}

export function useUpdateTemplate(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: Partial<Template>) => apiClient.put<Template>(`/admin/templates/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'templates'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'templates', id] })
    },
  })
}

export function useDeleteTemplate() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiClient.delete(`/admin/templates/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'templates'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'stats'] })
    },
  })
}

export function useUploadTemplateVideo(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (file: File) => {
      const formData = new FormData()
      formData.append('file', file)
      return apiClient.post<{ s3Key: string; videoUrl: string; transcoded: boolean }>(
        `/admin/templates/${id}/video`,
        formData,
      )
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'templates', id] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'templates'] })
    },
  })
}

export function useDeleteTemplateVideo(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => apiClient.delete(`/admin/templates/${id}/video`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'templates', id] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'templates'] })
    },
  })
}

export function useUploadTemplateThumbnail(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (dataUrl: string) =>
      apiClient.post<{ thumbnailS3Key: string; thumbnailUrl: string }>(
        `/admin/templates/${id}/thumbnail`,
        { dataUrl }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'templates', id] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'templates'] })
    },
  })
}

export function useDeleteTemplateThumbnail(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => apiClient.delete(`/admin/templates/${id}/thumbnail`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'templates', id] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'templates'] })
    },
  })
}

export function useAddTemplateFrameImages() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      templateId,
      images,
    }: {
      templateId: string
      images: Array<{ dataUrl: string; role: 'first_frame' | 'last_frame' }>
    }) => apiClient.post(`/admin/templates/${templateId}/frame-images`, { images }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'templates', variables.templateId] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'templates'] })
    },
  })
}

export function useDeleteFrameImage() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ templateId, frameImageId }: { templateId: string; frameImageId: string }) =>
      apiClient.delete(`/admin/templates/${templateId}/frame-images/${frameImageId}`),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'templates'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'templates', variables.templateId] })
    },
  })
}

// ==================== Hooks: Categories ====================

export function useCategories() {
  return useQuery({
    queryKey: ['admin', 'categories'],
    queryFn: () => apiClient.get<Category[]>('/admin/categories'),
  })
}

export function useCategory(id: string) {
  return useQuery({
    queryKey: ['admin', 'categories', id],
    queryFn: () => apiClient.get<Category>(`/admin/categories/${id}`),
    enabled: !!id,
  })
}

export function useCreateCategory() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: Partial<Category>) => apiClient.post<Category>('/admin/categories', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'categories'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'stats'] })
    },
  })
}

export function useUpdateCategory(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: Partial<Category>) => apiClient.put<Category>(`/admin/categories/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'categories'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'categories', id] })
    },
  })
}

export function useDeleteCategory() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiClient.delete(`/admin/categories/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'categories'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'stats'] })
    },
  })
}

// ==================== Hooks: Generations ====================

export function useGenerations(params: {
  page?: number
  pageSize?: number
  search?: string
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
}) {
  const query = new URLSearchParams()
  if (params.page) query.set('page', String(params.page))
  if (params.pageSize) query.set('pageSize', String(params.pageSize))
  if (params.search) query.set('search', params.search)
  if (params.sortBy) query.set('sortBy', params.sortBy)
  if (params.sortOrder) query.set('sortOrder', params.sortOrder)

  return useQuery({
    queryKey: ['admin', 'generations', params],
    queryFn: () => apiClient.get<PaginatedGenerations>(`/admin/generations?${query}`),
  })
}

export function useGenerationAttempts(params: {
  page?: number
  pageSize?: number
  search?: string
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
}) {
  const query = new URLSearchParams()
  if (params.page) query.set('page', String(params.page))
  if (params.pageSize) query.set('pageSize', String(params.pageSize))
  if (params.search) query.set('search', params.search)
  if (params.sortBy) query.set('sortBy', params.sortBy)
  if (params.sortOrder) query.set('sortOrder', params.sortOrder)

  return useQuery({
    queryKey: ['admin', 'generation-attempts', params],
    queryFn: () => apiClient.get<PaginatedGenerationAttempts>(`/admin/generations/attempts?${query}`),
  })
}

export function useGenerationAttempt(id: string) {
  return useQuery({
    queryKey: ['admin', 'generation-attempts', id],
    queryFn: () => apiClient.get<GenerationAttemptDetail>(`/admin/generations/attempts/${id}`),
    enabled: !!id,
  })
}

export function useDeleteGenerationAttempt() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiClient.delete(`/admin/generations/attempts/${id}`),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'generation-attempts'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'generation-attempts', id] })
    },
  })
}

export function useGeneration(id: string) {
  return useQuery({
    queryKey: ['admin', 'generations', id],
    queryFn: () => apiClient.get<Generation>(`/admin/generations/${id}`),
    enabled: !!id,
  })
}

export function useUpdateGeneration(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: Partial<Generation>) =>
      apiClient.put<Generation>(`/admin/generations/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'generations'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'generations', id] })
    },
  })
}

export function useDeleteGeneration() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiClient.delete(`/admin/generations/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'generations'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'stats'] })
    },
  })
}

// ==================== Types: Users ====================

export interface AdminUser {
  id: string
  email: string
  source: string
  isAdmin: boolean
  hasUsage: boolean
  enterpriseId: number | null
  workspaceId: number | null
  createdAt: string
  updatedAt?: string
}

export interface AdminUserDetail extends AdminUser {
  role: string | null
  /** 'oauth' = Wrks OAuth 2.0 grant (refresh token stored server-side), 'legacy' = wrksToken launch */
  authMode: 'oauth' | 'legacy'
  /** Masked (`…last6`) — the stored token is a live bearer credential and never leaves the backend */
  ssoTokenMasked: string | null
  tokenExpiresAt: string
  wrksRefreshExpiresAt: string | null
  wrksSub: string | null
}

export interface PaginatedUsers {
  items: AdminUser[]
  pagination: Pagination
}

// ==================== Types: Shared Videos ====================

export interface AdminSharedVideo {
  id: string
  userId: string
  userEmail: string | null
  generationId: string
  ratio: string
  videoUrl: string
  thumbnailUrl?: string | null
  thumbnailUrls?: string[]
  prompt: string | null
  createdAt: string
}

export interface PaginatedSharedVideos {
  items: AdminSharedVideo[]
  pagination: Pagination
}

// ==================== Hooks: Shared Videos ====================

export function useAdminSharedVideos(params: {
  page?: number
  pageSize?: number
  sortOrder?: 'asc' | 'desc'
}) {
  const query = new URLSearchParams()
  if (params.page) query.set('page', String(params.page))
  if (params.pageSize) query.set('pageSize', String(params.pageSize))
  if (params.sortOrder) query.set('sortOrder', params.sortOrder)

  return useQuery({
    queryKey: ['admin', 'shared-videos', params],
    queryFn: () => apiClient.get<PaginatedSharedVideos>(`/admin/shared-videos?${query}`),
  })
}

export interface AdminSharedVideoDetail extends AdminSharedVideo {
  generation: {
    id: string
    prompt: string | null
    ratio: string
    frameImages: Array<{
      s3Key: string
      thumbnailS3Key: string
      role: 'first_frame' | 'last_frame'
      imageUrl: string
      thumbnailUrl: string
    }> | null
    createdAt: string
  } | null
  template: {
    id: string
    name: string
    description: string | null
    thumbnailUrl: string | null
  } | null
}

export function useAdminSharedVideo(shareId: string) {
  return useQuery({
    queryKey: ['admin', 'shared-videos', shareId],
    queryFn: () => apiClient.get<AdminSharedVideoDetail>(`/admin/shared-videos/${shareId}`),
    enabled: !!shareId,
  })
}

export function useAdminDeleteSharedVideo() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (shareId: string) => apiClient.delete(`/admin/shared-videos/${shareId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'shared-videos'] })
    },
  })
}

// ==================== Hooks: Users ====================

export function useUsers(params: {
  page?: number
  pageSize?: number
  search?: string
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
}) {
  const query = new URLSearchParams()
  if (params.page) query.set('page', String(params.page))
  if (params.pageSize) query.set('pageSize', String(params.pageSize))
  if (params.search) query.set('search', params.search)
  if (params.sortBy) query.set('sortBy', params.sortBy)
  if (params.sortOrder) query.set('sortOrder', params.sortOrder)

  return useQuery({
    queryKey: ['admin', 'users', params],
    queryFn: () => apiClient.get<PaginatedUsers>(`/admin/users?${query}`),
  })
}

export function useUser(id: string) {
  return useQuery({
    queryKey: ['admin', 'users', id],
    queryFn: () => apiClient.get<AdminUserDetail>(`/admin/users/${id}`),
    enabled: !!id,
  })
}

export function useUpdateUser(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: { isAdmin: boolean }) =>
      apiClient.put<AdminUser>(`/admin/users/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'users', id] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'stats'] })
    },
  })
}

// ==================== Hooks: Settings ====================

export type SsoSource = 'gov' | 'wrks'

export interface CommunityHiddenEntry {
  source: SsoSource
  enterpriseId: number
}

export interface CommunityHiddenEnterprises {
  entries: CommunityHiddenEntry[]
}

export function useCommunityHiddenEnterprises() {
  return useQuery({
    queryKey: ['admin', 'settings', 'community-hidden-enterprises'],
    queryFn: () =>
      apiClient.get<CommunityHiddenEnterprises>('/admin/settings/community-hidden-enterprises'),
  })
}

export function useUpdateCommunityHiddenEnterprises() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (entries: CommunityHiddenEntry[]) =>
      apiClient.put<CommunityHiddenEnterprises>(
        '/admin/settings/community-hidden-enterprises',
        { entries },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['admin', 'settings', 'community-hidden-enterprises'],
      })
      // Invalidate caller's auth/me so menuHidden flag refreshes for the admin themselves
      queryClient.invalidateQueries({ queryKey: ['auth', 'me'] })
    },
  })
}

export interface OutageNotice {
  enabled: boolean
  message: string
}

export function useOutageNotice() {
  return useQuery({
    queryKey: ['admin', 'settings', 'outage-notice'],
    queryFn: () => apiClient.get<OutageNotice>('/admin/settings/outage-notice'),
  })
}

export function useUpdateOutageNotice() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (notice: OutageNotice) =>
      apiClient.put<OutageNotice>('/admin/settings/outage-notice', notice),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['admin', 'settings', 'outage-notice'],
      })
      // Public config carries the outage notice — refresh so banner updates immediately
      queryClient.invalidateQueries({ queryKey: ['config'] })
    },
  })
}

// ==================== Hooks: Error Reports ====================

export interface AdminErrorGroupLatest {
  id: string
  name: string
  message: string
  errorType: string
  url: string | null
  buildId: string | null
  status: string
  createdAt: string
}

export interface AdminErrorGroup {
  fingerprint: string
  count: number
  userCount: number
  firstSeen: string
  lastSeen: string
  latest: AdminErrorGroupLatest | null
}

export interface AdminErrorOccurrence {
  id: string
  userEmail: string | null
  url: string | null
  buildId: string | null
  origin: string
  createdAt: string
}

export interface AdminErrorReportDetail {
  id: string
  userId: string
  userEmail: string | null
  sessionId: string | null
  fingerprint: string
  name: string
  message: string
  stack: string | null
  // 압축된 stack 을 build 별 소스맵으로 복원한 결과 (백엔드가 조회 시 1회 채움). null = 복원 불가/미시도.
  stackSymbolicated: string | null
  errorType: string
  url: string | null
  route: string | null
  userAgent: string | null
  buildId: string | null
  language: string | null
  viewport: string | null
  breadcrumbs: { ts: number; type: string; message: string; data?: Record<string, unknown> }[] | null
  context: Record<string, unknown> | null
  origin: string
  status: string
  createdAt: string
  occurrences: AdminErrorOccurrence[]
}

export function useAdminErrorGroups(params: {
  page?: number
  pageSize?: number
  status?: string
  search?: string
  days?: number
}) {
  const query = new URLSearchParams()
  if (params.page) query.set('page', String(params.page))
  if (params.pageSize) query.set('pageSize', String(params.pageSize))
  if (params.status) query.set('status', params.status)
  if (params.search) query.set('search', params.search)
  if (params.days) query.set('days', String(params.days))

  return useQuery({
    queryKey: ['admin', 'error-reports', params],
    queryFn: () =>
      apiClient.get<{ items: AdminErrorGroup[]; total: number; page: number; pageSize: number }>(
        `/admin/error-reports?${query}`,
      ),
  })
}

export function useAdminErrorReport(id: string) {
  return useQuery({
    queryKey: ['admin', 'error-reports', id],
    queryFn: () => apiClient.get<AdminErrorReportDetail>(`/admin/error-reports/${id}`),
    enabled: !!id,
  })
}

export function useAdminResolveErrorFingerprint() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (fingerprint: string) =>
      apiClient.patch<{ fingerprint: string; status: string }>(
        `/admin/error-reports/fingerprints/${fingerprint}/resolve`,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'error-reports'] })
    },
  })
}

export function useAdminDeleteErrorFingerprint() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (fingerprint: string) =>
      apiClient.delete<{ fingerprint: string; deleted: number }>(
        `/admin/error-reports/fingerprints/${fingerprint}`,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'error-reports'] })
    },
  })
}

// AI template research and review

export type CandidateDraft = {
  name: string; description: string; categoryId: string; textPrompt: string; promptSuffix: string
  resolution: string; duration: number; ratio: string; referenceAssets: string[]
  nameI18n: Record<string, string>; descriptionI18n: Record<string, string>; textPromptI18n: Record<string, string>
}
export type CandidateEvaluation = {
  fidelity: number; visual: number; continuity: number; reusability: number
  criticalDefects: string[]; rationale: string
}
export type CandidateExperiment = {
  case: number; values: Record<string, string>; prompt: string; referenceAssets: string[]
  render: { jobId: string; videoAsset: string; frameAssets: string[]; provider: string; rewrittenPrompt: string | null; durationSeconds: number; width: number; height: number; hasAudio: boolean } | null; evaluation: CandidateEvaluation | null; error: string | null
}
export type CandidatePayload = {
  draft: CandidateDraft; purpose: string; sources: { url: string; reason: string }[]
  experiments: CandidateExperiment[]; thumbnailAsset: string | null; videoAsset: string | null
}
export type TemplateCandidate = {
  id: string; status: string; version: number; templateId: string | null
  createdAt: string; updatedAt: string; payload: CandidatePayload | null; passed: boolean | null
  costKrw: number; assets: { id: string; url: string | null }[]
}
export type TemplateCandidateDetailData = TemplateCandidate & {
  revisions: { version: number; payload: CandidatePayload; passed: boolean; createdAt: string }[]
  reviews: { version: number; action: string; note: string; createdAt: string }[]
}
export type TemplateAgentSummary = {
  pilotUsage: { spentKrw: number; videoAttempts: number }
  videoUsage: { attempts: number; waiting: number; processingSeconds: number }
  paused: boolean; weeklyLimitKrw: number; spentKrw: number; pending: number; submitted: number; published: number
  lastRun: { status: string; updatedAt: string; summary: { reason?: string; failures?: string[] } } | null
}
const TEMPLATE_CANDIDATES_ROOT = '/admin/template-candidates'

export function useTemplateCandidates(status: string, page: number) {
  return useQuery({ queryKey: ['template-candidates', status, page], queryFn: () => api.get<{ items: TemplateCandidate[]; total: number }>(`${TEMPLATE_CANDIDATES_ROOT}?status=${encodeURIComponent(status)}&page=${page}`), refetchInterval: 30000 })
}
export function useTemplateAgentSummary() {
  return useQuery({ queryKey: ['template-agent-summary'], queryFn: () => api.get<TemplateAgentSummary>(`${TEMPLATE_CANDIDATES_ROOT}/summary`), refetchInterval: 30000 })
}
export function useControlTemplateAgent() {
  const client = useQueryClient()
  return useMutation({ mutationFn: (paused: boolean) => api.patch(`${TEMPLATE_CANDIDATES_ROOT}/summary`, { paused }), onSuccess: () => client.invalidateQueries({ queryKey: ['template-agent-summary'] }) })
}
export function useTemplateCandidate(id: string) {
  return useQuery({ queryKey: ['template-candidates', id], queryFn: () => api.get<TemplateCandidateDetailData>(`${TEMPLATE_CANDIDATES_ROOT}/${id}`) })
}
export function useReviewTemplateCandidate(id: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (body: { action: string; expectedVersion: number; note: string; draft?: CandidateDraft; thumbnailAsset?: string; videoAsset?: string; playbackConfirmed?: boolean }) => api.post<{ templateId: string | null }>(`${TEMPLATE_CANDIDATES_ROOT}/${id}/review`, body),
    onSettled: () => {
      void client.invalidateQueries({ queryKey: ['template-candidates'] })
      void client.invalidateQueries({ queryKey: ['template-agent-summary'] })
      void client.invalidateQueries({ queryKey: ['admin', 'templates'] })
      void client.invalidateQueries({ queryKey: ['templates'] })
    },
  })
}
